import RichTextBlock from "@/views/projects/ProjectEdit/Blocks/RichTextBlock";
import VideoBlock from "@/views/projects/ProjectEdit/Blocks/VideoBlock";
import QuizBlock from "@/views/projects/ProjectEdit/Blocks/QuizBlock";
import EmbedBlock from "@/views/projects/ProjectEdit/Blocks/EmbedBlock";
import ImageBlock from "@/views/projects/ProjectEdit/Blocks/ImageBlock";
import FileBlock from "@/views/projects/ProjectEdit/Blocks/FileBlock";
import {FILE_MAX_COUNT} from "@/views/projects/ProjectEdit/Blocks/fileRules";
import {isHttpsUrl, isValidEmbedHeight, urlHost} from "@/views/projects/ProjectEdit/Blocks/embedRules";
import lmsFeatures from "@/config/lmsFeatures";

/*
 * Registro de tipos de bloque del editor de lecciones, espejo del
 * `LessonBlockTypeRegistry` del backend (LMS-067). Añadir un tipo nuevo es
 * añadir una entrada aquí; el editor no cambia.
 *
 * Contrato de cada tipo:
 *  - component:  componente que edita `data` con v-model (`modelValue` + `update:modelValue`)
 *                y recibe las props `blockId` (prefijo de ids del DOM) y `lessonUuid`.
 *  - labelKey:   clave i18n del nombre del tipo.
 *  - descriptionKey, icon: descripción de una línea e icono de Font Awesome del menú «Añadir bloque».
 *  - summary:    (opcional) texto corto de `data` para la cabecera del bloque plegado.
 *  - duplicable: (opcional, `true` por defecto) si se puede duplicar el bloque.
 *  - createData: datos iniciales de un bloque nuevo.
 *  - validate:   devuelve la clave i18n del error o null si `data` es válido.
 *  - toPayload:  (opcional) datos que se envían a la API, sin campos solo de lectura.
 *  - fromResponse: (opcional) `(data, bloqueDelBackend)`: mezcla en `data` lo que devuelve
 *                el backend al cargar y al guardar (campos calculados, `preview`…).
 *  - serverError:  (opcional) traduce los errores 422 de `data.*` del bloque a { key, params }
 *                o null; si no lo traduce, el editor muestra el mensaje del backend.
 */
const stripHtml = (html) => (html ?? '').replace(/<[^>]*>/g, '').trim()
// Una imagen o un iframe también son contenido aunque no haya texto (E12); el backend conserva ambos.
const hasMedia = (html) => /<(img|iframe)\b/i.test(html ?? '')
const EMBED_SOURCES = ['youtube', 'vimeo']
const SUMMARY_LENGTH = 60

const truncate = (text) => text.length > SUMMARY_LENGTH ? text.slice(0, SUMMARY_LENGTH).trimEnd() + '…' : text
// Texto plano del HTML sin ejecutar nada (DOMParser no carga recursos ni scripts).
const plainText = (html) => (new DOMParser().parseFromString(html ?? '', 'text/html').body.textContent ?? '').replace(/\s+/g, ' ').trim()
const urlSummary = (url) => (url ?? '').replace(/^https?:\/\//, '')
const fileName = (file) => (file.name ?? '').trim() || file.original_name || ''

const blockTypes = {
    rich_text: {
        component: RichTextBlock,
        labelKey: 'projects.block_type_rich_text',
        descriptionKey: 'projects.block_type_rich_text_description',
        icon: 'align-left',
        summary: (data) => truncate(plainText(data.html)),
        createData: () => ({html: ''}),
        validate: (data) => stripHtml(data.html) || hasMedia(data.html) ? null : 'projects.block_rich_text_required',
    },
    embed: {
        component: EmbedBlock,
        labelKey: 'projects.block_type_embed',
        descriptionKey: 'projects.block_type_embed_description',
        icon: 'globe',
        summary: (data) => truncate(urlSummary(data.url)),
        createData: () => ({url: '', height: null}),
        validate: (data) => {
            if (!isHttpsUrl(data.url)) return 'projects.block_embed_url_invalid'
            return isValidEmbedHeight(data.height) ? null : 'projects.block_embed_height_invalid'
        },
        // `sandbox` lo decide el backend (lms.embed_sandbox): no se envía, se recibe.
        toPayload: (data) => ({url: data.url, height: data.height ?? null}),
        fromResponse: (data, saved) => ({...data, sandbox: saved?.data?.sandbox ?? null}),
        serverError: (errors, data) => errors['data.url']
            ? {key: 'projects.block_embed_domain_not_allowed', params: {host: urlHost(data.url)}}
            : null,
    },
    // Imagen (FE-057 · LMS-071). Va detrás de `embed` por el orden de tipos decidido (D2).
    image: {
        component: ImageBlock,
        labelKey: 'projects.block_type_image',
        descriptionKey: 'projects.block_type_image_description',
        icon: 'image',
        summary: (data) => truncate((data.alt ?? '').trim()),
        createData: () => ({file_uuid: null, alt: '', caption: ''}),
        validate: (data) => {
            if (!data.file_uuid) return 'projects.block_image_required'
            return (data.alt ?? '').trim() ? null : 'projects.block_image_alt_required'
        },
        // `preview_url` es solo para el editor: la URL temporal la da el backend en `preview`.
        toPayload: (data) => ({file_uuid: data.file_uuid, alt: (data.alt ?? '').trim(), caption: (data.caption ?? '').trim() || null}),
        fromResponse: (data, saved) => ({...data, preview_url: saved?.preview?.url ?? data.preview_url ?? null}),
        serverError: (errors) => {
            if (errors['data.alt']) return {key: 'projects.block_image_alt_required'}
            return errors['data.file_uuid'] ? {key: 'projects.block_image_file_invalid'} : null
        },
    },
    // Archivos descargables (FE-058 · LMS-070). Tercer tipo nuevo, detrás de `image` (D2).
    file: {
        component: FileBlock,
        labelKey: 'projects.block_type_file',
        descriptionKey: 'projects.block_type_file_description',
        icon: 'file-alt',
        summary: (data) => truncate((data.files ?? []).map(fileName).filter(Boolean).join(', ')),
        createData: () => ({files: []}),
        validate: (data) => {
            const count = (data.files ?? []).length
            if (!count) return 'projects.block_file_required'
            return count > FILE_MAX_COUNT ? 'projects.block_file_too_many' : null
        },
        // `original_name`, `size` y `url` son solo para el editor; `name` vacío = nombre original.
        toPayload: (data) => ({
            files: (data.files ?? []).map((file) => ({file_uuid: file.file_uuid, name: (file.name ?? '').trim() || null})),
        }),
        /*
         * `preview.files` trae nombre visible, tamaño y URL de cada fichero que sigue
         * existiendo. Si el fichero no tiene etiqueta propia, el nombre visible es el original.
         */
        fromResponse: (data, saved) => {
            const previews = Object.fromEntries((saved?.preview?.files ?? []).map((file) => [file.file_uuid, file]))
            return {
                ...data,
                files: (data.files ?? []).map((file) => {
                    const preview = previews[file.file_uuid]
                    // Con `preview` pero sin el fichero: se ha borrado o no es de la academia.
                    if (!preview) return saved?.preview ? {...file, name: file.name ?? '', missing: true} : file
                    const hasCustomName = Boolean((file.name ?? '').trim())
                    return {
                        ...file,
                        name: file.name ?? '',
                        original_name: hasCustomName ? (file.original_name ?? null) : preview.name,
                        size: preview.size ?? file.size ?? null,
                        url: preview.url ?? file.url ?? null,
                    }
                }),
            }
        },
        serverError: (errors) => {
            const fields = Object.keys(errors)
            if (fields.some((field) => /^data\.files\.\d+\.name$/.test(field))) return {key: 'projects.block_file_name_invalid'}
            if (fields.some((field) => field.startsWith('data.files'))) return {key: 'projects.block_file_invalid'}
            return null
        },
    },
    video: {
        component: VideoBlock,
        labelKey: 'projects.block_type_video',
        descriptionKey: 'projects.block_type_video_description',
        icon: 'video',
        summary: (data) => truncate(urlSummary(data.url)),
        createData: () => ({source: null}),
        validate: (data) => (data.source === 's3' && data.file_id) || (EMBED_SOURCES.includes(data.source) && data.url)
            ? null
            : 'projects.block_video_required',
        // `playback_url` y `embed_url` los calcula el backend (LMS-069).
        toPayload: (data) => data.source === 's3'
            ? {source: data.source, file_id: data.file_id}
            : {source: data.source, url: data.url},
    },
    quiz: {
        component: QuizBlock,
        labelKey: 'projects.block_type_quiz',
        descriptionKey: 'projects.block_type_quiz_description',
        icon: 'question-circle',
        // Dos bloques no deben compartir el mismo quiz.
        duplicable: false,
        createData: () => ({quiz_id: null}),
        validate: (data) => data.quiz_id ? null : 'projects.block_quiz_required',
    },
}

// Tipos que dependen de endpoints aún no publicados (ver src/config/lmsFeatures.js).
if (!lmsFeatures.videoBlock) delete blockTypes.video
if (!lmsFeatures.quizBlock) delete blockTypes.quiz

export default blockTypes
